/**
 * A-One Testimonials
 * Continuous RTL marquee (when [data-aone-testimonials-carousel] is present),
 * drag / touch / wheel scrolling, and accessible “Read more” overlay.
 */
(function () {
  'use strict';

  if (window.__aoneTestimonialsInit) return;
  window.__aoneTestimonialsInit = true;

  var SECTION_SELECTOR = '[data-aone-testimonials]';
  var DRAG_CLICK_THRESHOLD = 6;
  var MOBILE_MAX_WIDTH = 749;
  var instances = new Map();

  /**
   * @param {string|null|undefined} value
   * @param {boolean} fallback
   */
  function parseBoolAttr(value, fallback) {
    if (value == null || value === '') return fallback;
    var v = String(value).toLowerCase();
    if (v === 'false' || v === '0' || v === 'off') return false;
    if (v === 'true' || v === '1' || v === 'on') return true;
    return fallback;
  }

  /**
   * @param {string|null|undefined} value
   * @param {number} fallback
   */
  function parseNumberAttr(value, fallback) {
    var n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  /**
   * Clones stay in the accessibility tree as aria-hidden and are skipped by Tab,
   * but must remain clickable - marquee users often click a duplicated card.
   * Do NOT use `inert` here; it blocks pointer events on visible clones.
   * @param {Element} el
   * @param {boolean} hidden
   */
  function setCloneA11y(el, hidden) {
    if (!(el instanceof HTMLElement)) return;
    if (hidden) {
      el.setAttribute('aria-hidden', 'true');
      el.removeAttribute('inert');
    } else {
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
    }
    var focusables = el.querySelectorAll('a, button, input, textarea, select, [tabindex]');
    for (var i = 0; i < focusables.length; i++) {
      var node = focusables[i];
      if (!(node instanceof HTMLElement)) continue;
      if (hidden) {
        if (!node.hasAttribute('data-aone-prev-tabindex')) {
          node.setAttribute(
            'data-aone-prev-tabindex',
            node.hasAttribute('tabindex') ? String(node.getAttribute('tabindex')) : ''
          );
        }
        node.setAttribute('tabindex', '-1');
      } else if (node.hasAttribute('data-aone-prev-tabindex')) {
        var prev = node.getAttribute('data-aone-prev-tabindex');
        if (prev === '') node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', prev);
        node.removeAttribute('data-aone-prev-tabindex');
      }
    }
  }

  /**
   * @param {Element} root
   */
  function stripCloneIds(root) {
    if (!(root instanceof Element)) return;
    root.removeAttribute('id');
    var withIds = root.querySelectorAll('[id]');
    for (var i = 0; i < withIds.length; i++) {
      withIds[i].removeAttribute('id');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function AoneTestimonials(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || root.id || '';
    this.carousel = root.querySelector('[data-aone-testimonials-carousel]');
    this.viewport = this.carousel
      ? this.carousel.querySelector('[data-carousel-viewport]')
      : null;
    this.track = this.carousel ? this.carousel.querySelector('[data-carousel-track]') : null;
    this.modal = root.querySelector('[data-review-modal]');
    this.modalDialog = this.modal ? this.modal.querySelector('[data-review-modal-dialog]') : null;
    this.modalStars = this.modal ? this.modal.querySelector('[data-modal-stars]') : null;
    this.modalGoogle = this.modal ? this.modal.querySelector('[data-modal-google]') : null;
    this.modalBody = this.modal ? this.modal.querySelector('[data-modal-body]') : null;
    this.modalName = this.modal ? this.modal.querySelector('[data-modal-name]') : null;
    this.modalCloseBtns = this.modal
      ? Array.prototype.slice.call(this.modal.querySelectorAll('[data-modal-close]'))
      : [];

    this.enableAutoplay = parseBoolAttr(
      root.getAttribute('data-auto-scroll') != null
        ? root.getAttribute('data-auto-scroll')
        : root.getAttribute('data-autoplay'),
      true
    );
    this.speed = Math.max(
      5,
      Math.min(100, parseNumberAttr(root.getAttribute('data-scroll-speed'), 25))
    );
    this.pauseOnHover = parseBoolAttr(root.getAttribute('data-pause-on-hover'), true);
    this.interactionPauseMs =
      Math.max(1, Math.min(10, parseNumberAttr(root.getAttribute('data-interaction-pause'), 3))) *
      1000;
    this.disableMobile = parseBoolAttr(root.getAttribute('data-disable-mobile'), false);
    this.isMobileViewport = false;

    this.reducedMotion = false;
    this.loopWidth = 0;
    this.rafId = 0;
    this.lastTs = 0;
    this.autoplayActive = false;
    this.paused = false;
    this.userInteracting = false;
    this.idleTimer = 0;
    this.inView = true;
    this.tabHidden = typeof document.hidden === 'boolean' ? document.hidden : false;
    this.modalOpen = false;
    this.lastFocused = null;
    this.bodyScrollLocked = false;
    this.prevBodyOverflow = '';
    this.scrollDirection = 1;

    this.dragging = false;
    this.dragMoved = false;
    this.dragAxis = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragStartScroll = 0;
    this.activePointerId = null;
    this.suppressClick = false;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onSelectStart = this.onSelectStart.bind(this);
    this.onReadMoreClick = this.onReadMoreClick.bind(this);
    this.onModalKeydown = this.onModalKeydown.bind(this);
    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.onSelectionChange = this.onSelectionChange.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onMobileQueryChange = this.onMobileQueryChange.bind(this);
    this.tick = this.tick.bind(this);
    this.checkReviewOverflow = this.checkReviewOverflow.bind(this);

    this.updateScrollDirection();
    this.updateMobileViewport();

    if (typeof matchMedia === 'function') {
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = !!this.motionQuery.matches;
      if (typeof this.motionQuery.addEventListener === 'function') {
        this.motionQuery.addEventListener('change', this.onMotionChange);
      } else if (typeof this.motionQuery.addListener === 'function') {
        this.motionQuery.addListener(this.onMotionChange);
      }

      this.mobileQuery = matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)');
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.onMobileQueryChange);
      } else if (typeof this.mobileQuery.addListener === 'function') {
        this.mobileQuery.addListener(this.onMobileQueryChange);
      }
    }

    document.addEventListener('visibilitychange', this.onVisibility);

    this.resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(this.onResize) : null;
    if (this.resizeObserver) {
      this.resizeObserver.observe(this.root);
      if (this.viewport) this.resizeObserver.observe(this.viewport);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if (typeof IntersectionObserver !== 'undefined' && this.carousel) {
      this.intersectionObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].target === this.carousel) {
              this.inView = entries[i].isIntersecting;
              this.syncAutoplay();
            }
          }
        }.bind(this),
        { root: null, rootMargin: '80px 0px', threshold: 0 }
      );
      this.intersectionObserver.observe(this.carousel);
    } else {
      this.inView = true;
    }

    this.root.addEventListener('click', this.onReadMoreClick);
    document.addEventListener('keydown', this.onDocKeydown);
    document.addEventListener('selectionchange', this.onSelectionChange);

    if (this.modal) {
      for (var c = 0; c < this.modalCloseBtns.length; c++) {
        this.modalCloseBtns[c].addEventListener('click', this.closeReviewModal.bind(this));
      }
    }

    if (this.viewport && this.track) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('lostpointercapture', this.onPointerUp);
      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('mouseenter', this.onMouseEnter);
      this.viewport.addEventListener('mouseleave', this.onMouseLeave);
      this.viewport.addEventListener('focusin', this.onFocusIn);
      this.viewport.addEventListener('focusout', this.onFocusOut);
      this.viewport.addEventListener('selectstart', this.onSelectStart);
    }

    this.bindFontLoad();
    this.checkReviewOverflow();
    if (this.viewport && this.track) this.setupInfiniteLoop();
    this.syncAutoplay();
  }

  AoneTestimonials.prototype.destroy = function () {
    this.stopAutoplay();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = 0;
    }
    this.unlockBodyScroll();
    if (this.modalOpen) this.closeReviewModal(true);

    this.root.removeEventListener('click', this.onReadMoreClick);
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('selectionchange', this.onSelectionChange);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this._boundFontCheck && document.fonts) {
      document.fonts.removeEventListener('loadingdone', this._boundFontCheck);
    }

    if (this.viewport) {
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport.removeEventListener('lostpointercapture', this.onPointerUp);
      this.viewport.removeEventListener('wheel', this.onWheel);
      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('mouseenter', this.onMouseEnter);
      this.viewport.removeEventListener('mouseleave', this.onMouseLeave);
      this.viewport.removeEventListener('focusin', this.onFocusIn);
      this.viewport.removeEventListener('focusout', this.onFocusOut);
      this.viewport.removeEventListener('selectstart', this.onSelectStart);
    }

    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.onResize);

    if (this.intersectionObserver) this.intersectionObserver.disconnect();

    if (this.motionQuery) {
      if (typeof this.motionQuery.removeEventListener === 'function') {
        this.motionQuery.removeEventListener('change', this.onMotionChange);
      } else if (typeof this.motionQuery.removeListener === 'function') {
        this.motionQuery.removeListener(this.onMotionChange);
      }
    }

    if (this.mobileQuery) {
      if (typeof this.mobileQuery.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.onMobileQueryChange);
      } else if (typeof this.mobileQuery.removeListener === 'function') {
        this.mobileQuery.removeListener(this.onMobileQueryChange);
      }
    }

    this.removeClones();

    if (this.root && this.root._aoneTestimonialsController === this) {
      this.root._aoneTestimonialsController = null;
    }
  };

  AoneTestimonials.prototype.bindFontLoad = function () {
    this._boundFontCheck = function () {
      this.checkReviewOverflow();
      if (this.track) this.setupInfiniteLoop();
      this.syncAutoplay();
    }.bind(this);

    if (document.fonts && typeof document.fonts.ready === 'object') {
      document.fonts.ready.then(this._boundFontCheck).catch(function () {});
      if (typeof document.fonts.addEventListener === 'function') {
        document.fonts.addEventListener('loadingdone', this._boundFontCheck);
      }
    } else {
      window.setTimeout(this._boundFontCheck, 300);
    }
  };

  AoneTestimonials.prototype.onMotionChange = function (e) {
    this.reducedMotion = !!(e && e.matches);
    this.syncAutoplay();
  };

  AoneTestimonials.prototype.onMobileQueryChange = function () {
    this.updateMobileViewport();
    this.syncAutoplay();
  };

  AoneTestimonials.prototype.updateMobileViewport = function () {
    if (this.mobileQuery) {
      this.isMobileViewport = !!this.mobileQuery.matches;
    } else {
      this.isMobileViewport =
        typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;
    }
  };

  AoneTestimonials.prototype.updateScrollDirection = function () {
    // LTR: increasing scrollLeft moves content right→left.
    // RTL: reverse the delta so the visible travel stays right→left.
    var dir = 'ltr';
    try {
      var node = this.viewport || this.root;
      if (node) dir = window.getComputedStyle(node).direction || 'ltr';
    } catch (err) {
      dir = 'ltr';
    }
    this.scrollDirection = dir === 'rtl' ? -1 : 1;
  };

  AoneTestimonials.prototype.onVisibility = function () {
    this.tabHidden = !!document.hidden;
    this.syncAutoplay();
  };

  AoneTestimonials.prototype.onResize = function () {
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = requestAnimationFrame(
      function () {
        this._resizeRaf = 0;
        var wasPlaying = this.autoplayActive;
        this.stopAutoplay();
        this.updateScrollDirection();
        this.updateMobileViewport();
        this.checkReviewOverflow();
        if (this.track) this.setupInfiniteLoop();
        if (wasPlaying || this.canAutoplay()) this.syncAutoplay();
      }.bind(this)
    );
  };

  AoneTestimonials.prototype.removeClones = function () {
    if (!this.track) return;
    var clones = this.track.querySelectorAll('[data-aone-clone="true"]');
    for (var i = clones.length - 1; i >= 0; i--) {
      clones[i].parentNode.removeChild(clones[i]);
    }
  };

  AoneTestimonials.prototype.getOriginalCards = function () {
    if (!this.track) return [];
    return Array.prototype.slice.call(this.track.children).filter(function (el) {
      return el.nodeType === 1 && el.getAttribute('data-aone-clone') !== 'true';
    });
  };

  AoneTestimonials.prototype.setupInfiniteLoop = function () {
    if (!this.viewport || !this.track) return;

    var previousScroll = this.viewport.scrollLeft;
    var previousLoop = this.loopWidth || 0;
    var progress = previousLoop > 0 ? previousScroll % previousLoop : previousScroll;

    this.removeClones();
    var originals = this.getOriginalCards();
    if (originals.length === 0) {
      this.loopWidth = 0;
      return;
    }

    // Force layout before measuring.
    void this.track.offsetWidth;
    var baseWidth = this.track.scrollWidth;
    var viewWidth = this.viewport.clientWidth;
    if (baseWidth <= 0) {
      this.loopWidth = 0;
      return;
    }

    var setsNeeded = 2;
    if (viewWidth > 0) {
      setsNeeded = Math.max(2, Math.ceil((viewWidth * 2) / baseWidth) + 1);
    }

    for (var s = 1; s < setsNeeded; s++) {
      for (var i = 0; i < originals.length; i++) {
        var clone = originals[i].cloneNode(true);
        clone.setAttribute('data-aone-clone', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        stripCloneIds(clone);
        setCloneA11y(clone, true);
        this.track.appendChild(clone);
      }
    }

    this.loopWidth = baseWidth;
    var nextScroll = progress;
    if (nextScroll < 0) nextScroll = 0;
    if (this.loopWidth > 0 && nextScroll >= this.loopWidth) {
      nextScroll = nextScroll % this.loopWidth;
    }
    this._ignoreScroll = true;
    this.viewport.scrollLeft = nextScroll;
    this._ignoreScroll = false;
  };

  AoneTestimonials.prototype.normalizeScroll = function () {
    if (!this.viewport || this.loopWidth <= 0) return;
    var left = this.viewport.scrollLeft;
    var next = left;
    if (next >= this.loopWidth) {
      next = next - this.loopWidth * Math.floor(next / this.loopWidth);
    } else if (next < 0) {
      next = next + this.loopWidth * Math.ceil(-next / this.loopWidth);
    }
    if (next !== left) {
      var prevIgnore = this._ignoreScroll;
      this._ignoreScroll = true;
      this.viewport.scrollLeft = next;
      this._ignoreScroll = prevIgnore;
    }
  };

  AoneTestimonials.prototype.canAutoplay = function () {
    return (
      !!this.viewport &&
      !!this.track &&
      this.enableAutoplay &&
      !this.reducedMotion &&
      !(this.disableMobile && this.isMobileViewport) &&
      !this.paused &&
      !this.userInteracting &&
      !this.dragging &&
      !this.modalOpen &&
      this.inView &&
      !this.tabHidden &&
      this.loopWidth > 0
    );
  };

  AoneTestimonials.prototype.syncAutoplay = function () {
    if (this.canAutoplay()) this.startAutoplay();
    else this.stopAutoplay();
  };

  AoneTestimonials.prototype.startAutoplay = function () {
    if (this.autoplayActive) return;
    if (!this.canAutoplay()) return;
    this.autoplayActive = true;
    this.lastTs = 0;
    this.rafId = requestAnimationFrame(this.tick);
  };

  AoneTestimonials.prototype.stopAutoplay = function () {
    this.autoplayActive = false;
    this.lastTs = 0;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  };

  AoneTestimonials.prototype.tick = function (ts) {
    if (!this.autoplayActive) return;
    if (!this.canAutoplay()) {
      this.stopAutoplay();
      return;
    }
    if (!this.lastTs) this.lastTs = ts;
    var dt = Math.min(64, ts - this.lastTs) / 1000;
    this.lastTs = ts;
    var distance = this.speed * dt * this.scrollDirection;

    // Programmatic scroll must not be treated as user interaction
    // (scroll events would otherwise pause autoplay every frame).
    this._ignoreScroll = true;
    this.viewport.scrollLeft += distance;
    this.normalizeScroll();
    this._ignoreScroll = false;

    this.rafId = requestAnimationFrame(this.tick);
  };

  AoneTestimonials.prototype.pauseForInteraction = function () {
    this.userInteracting = true;
    this.stopAutoplay();
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = 0;
  };

  AoneTestimonials.prototype.resumeAfterIdle = function () {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(
      function () {
        this.idleTimer = 0;
        this.userInteracting = false;
        this.syncAutoplay();
      }.bind(this),
      this.interactionPauseMs || 3000
    );
  };

  AoneTestimonials.prototype.onMouseEnter = function () {
    if (!this.pauseOnHover) return;
    this.paused = true;
    this.stopAutoplay();
  };

  AoneTestimonials.prototype.onMouseLeave = function () {
    if (!this.pauseOnHover) return;
    this.paused = false;
    if (!this.dragging && !this.userInteracting) this.syncAutoplay();
    else this.resumeAfterIdle();
  };

  AoneTestimonials.prototype.onFocusIn = function () {
    this.pauseForInteraction();
  };

  AoneTestimonials.prototype.onFocusOut = function () {
    this.resumeAfterIdle();
  };

  AoneTestimonials.prototype.onSelectStart = function (e) {
    if (this.dragging && this.dragMoved) {
      e.preventDefault();
    }
  };

  AoneTestimonials.prototype.onScroll = function () {
    // Ignore scroll events caused by requestAnimationFrame autoplay / loop normalize.
    if (this._ignoreScroll || this.autoplayActive) return;
    this.normalizeScroll();
    if (!this.dragging) {
      this.pauseForInteraction();
      this.resumeAfterIdle();
    }
  };

  AoneTestimonials.prototype.onWheel = function (e) {
    if (!this.viewport || !e) return;
    var absX = Math.abs(e.deltaX);
    var absY = Math.abs(e.deltaY);
    // Trackpad horizontal or shift+wheel → scroll the track; otherwise let page scroll.
    if (absX > absY || e.shiftKey) {
      e.preventDefault();
      this.pauseForInteraction();
      this.viewport.scrollLeft += e.shiftKey && absX < absY ? e.deltaY : e.deltaX;
      this.normalizeScroll();
      this.resumeAfterIdle();
    }
  };

  AoneTestimonials.prototype.onPointerDown = function (e) {
    if (!e || !this.viewport) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    // Touch/pen: use native overflow-x scrolling (touch-action: pan-x pan-y).
    // Mouse-only custom drag avoids fighting the browser swipe gesture.
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (e.target && e.target.closest && e.target.closest('a, button, input, textarea, select, label')) {
      return;
    }

    this.dragging = true;
    this.dragMoved = false;
    this.dragAxis = null;
    this.suppressClick = false;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartScroll = this.viewport.scrollLeft;
    this.activePointerId = e.pointerId;
    this.pauseForInteraction();
  };

  AoneTestimonials.prototype.onPointerMove = function (e) {
    if (!this.dragging || !this.viewport) return;
    if (this.activePointerId != null && e.pointerId !== this.activePointerId) return;

    var dx = e.clientX - this.dragStartX;
    var dy = e.clientY - this.dragStartY;

    if (!this.dragAxis) {
      if (Math.abs(dx) < DRAG_CLICK_THRESHOLD && Math.abs(dy) < DRAG_CLICK_THRESHOLD) return;
      // Prefer vertical page scroll when the gesture is mostly vertical.
      if (Math.abs(dy) > Math.abs(dx)) {
        this.dragging = false;
        this.dragAxis = 'y';
        this.activePointerId = null;
        this.resumeAfterIdle();
        return;
      }
      this.dragAxis = 'x';
      this.dragMoved = true;
      this.suppressClick = true;
      this.viewport.classList.add('is-dragging');
      this.viewport.classList.add('is-grabbing');
      try {
        this.viewport.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
    }

    if (this.dragAxis !== 'x') return;

    this.viewport.scrollLeft = this.dragStartScroll - dx;
    this.normalizeScroll();
  };

  AoneTestimonials.prototype.onPointerUp = function (e) {
    if (!this.dragging && this.dragAxis !== 'x') {
      this.dragging = false;
      this.dragAxis = null;
      return;
    }
    if (e && this.activePointerId != null && e.pointerId !== this.activePointerId) return;

    var wasHorizontalDrag = this.dragAxis === 'x' && this.dragMoved;
    this.dragging = false;
    this.dragAxis = null;
    this.viewport.classList.remove('is-dragging');
    this.viewport.classList.remove('is-grabbing');
    if (this.activePointerId != null) {
      try {
        this.viewport.releasePointerCapture(this.activePointerId);
      } catch (err) {
        /* ignore */
      }
    }
    this.activePointerId = null;
    this.resumeAfterIdle();

    if (wasHorizontalDrag && this.suppressClick) {
      var suppress = function (evt) {
        // Never block intentional Read more activations after a drag ends.
        if (evt.target && evt.target.closest && evt.target.closest('[data-read-more]')) {
          this.viewport.removeEventListener('click', suppress, true);
          return;
        }
        evt.preventDefault();
        evt.stopPropagation();
        this.viewport.removeEventListener('click', suppress, true);
      }.bind(this);
      this.viewport.addEventListener('click', suppress, true);
      window.setTimeout(
        function () {
          this.viewport.removeEventListener('click', suppress, true);
          this.suppressClick = false;
        }.bind(this),
        0
      );
    } else {
      this.suppressClick = false;
    }
  };

  AoneTestimonials.prototype.checkReviewOverflow = function () {
    var texts = this.root.querySelectorAll('[data-review-text]');
    for (var i = 0; i < texts.length; i++) {
      var el = texts[i];
      if (!(el instanceof HTMLElement)) continue;
      if (el.closest('[data-aone-clone="true"]')) continue;

      var wrap = el.closest('[data-review-wrap]') || el.parentElement;
      var btn = wrap ? wrap.querySelector('[data-read-more]') : null;
      if (!btn) continue;

      var styles = window.getComputedStyle(el);
      var lineHeight = parseFloat(styles.lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        lineHeight = parseFloat(styles.fontSize) * 1.3;
      }
      var maxH = Math.round(lineHeight * 5);
      if (maxH <= 0) maxH = el.clientHeight;

      var probe = el.cloneNode(true);
      probe.removeAttribute('id');
      probe.setAttribute('aria-hidden', 'true');
      probe.className = el.className;
      probe.classList.add('is-measuring');
      probe.style.cssText =
        'position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;' +
        'display:block;-webkit-line-clamp:unset;line-clamp:unset;-webkit-box-orient:initial;' +
        'height:auto;max-height:none;overflow:visible;width:' +
        el.clientWidth +
        'px;';
      document.body.appendChild(probe);
      var overflowing = probe.scrollHeight > maxH + 1;
      document.body.removeChild(probe);

      if (overflowing) {
        btn.hidden = false;
        btn.setAttribute('aria-expanded', 'false');
        var name = btn.getAttribute('data-reviewer-name') || '';
        var label = name ? 'Read more of review by ' + name : 'Read more of this review';
        btn.setAttribute('aria-label', label);
      } else {
        btn.hidden = true;
        btn.removeAttribute('aria-label');
        btn.setAttribute('aria-expanded', 'false');
      }
    }
  };

  AoneTestimonials.prototype.onReadMoreClick = function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-read-more]') : null;
    if (!btn || !this.root.contains(btn)) return;
    if (btn.hidden) return;
    e.preventDefault();
    e.stopPropagation();
    this.openReviewModal(btn);
  };

  /**
   * Prefer focusing the original (non-clone) Read more control after the modal closes.
   * @param {HTMLElement} btn
   * @returns {HTMLElement}
   */
  AoneTestimonials.prototype.resolveFocusButton = function (btn) {
    if (!btn) return btn;
    var card = btn.closest('[data-review-card]');
    if (!card || card.getAttribute('data-aone-clone') !== 'true') return btn;

    var name = btn.getAttribute('data-reviewer-name') || '';
    var textEl = card.querySelector('[data-review-text]');
    var text = textEl ? textEl.textContent : '';
    var originals = this.root.querySelectorAll(
      '[data-review-card]:not([data-aone-clone="true"]) [data-read-more]'
    );
    for (var i = 0; i < originals.length; i++) {
      var candidate = originals[i];
      if (candidate.hidden) continue;
      if (name && candidate.getAttribute('data-reviewer-name') === name) return candidate;
      var wrap = candidate.closest('[data-review-wrap]');
      var body = wrap ? wrap.querySelector('[data-review-text]') : null;
      if (text && body && body.textContent === text) return candidate;
    }
    return btn;
  };

  AoneTestimonials.prototype.openReviewModal = function (btn) {
    if (!this.modal || !btn) return;
    var card = btn.closest('[data-review-card]');
    if (!card) return;

    var stars = card.querySelector('[data-card-stars]');
    var google = card.querySelector('[data-card-google]');
    var text = card.querySelector('[data-review-text]');
    var nameEl = card.querySelector('[data-card-name]');
    var name = btn.getAttribute('data-reviewer-name') || (nameEl ? nameEl.textContent : '') || '';

    if (this.modalStars) {
      this.modalStars.innerHTML = stars ? stars.innerHTML : '';
    }
    if (this.modalGoogle) {
      if (google && google.innerHTML.trim()) {
        this.modalGoogle.hidden = false;
        this.modalGoogle.innerHTML = google.innerHTML;
      } else {
        this.modalGoogle.hidden = true;
        this.modalGoogle.innerHTML = '';
      }
    }
    if (this.modalBody) {
      this.modalBody.innerHTML = text ? text.innerHTML : '';
    }
    if (this.modalName) {
      this.modalName.textContent = name ? '- ' + name.replace(/^\s*-\s*/, '') : '';
    }

    this.lastFocused = this.resolveFocusButton(btn);
    if (btn.setAttribute) btn.setAttribute('aria-expanded', 'true');
    if (this.lastFocused && this.lastFocused !== btn) {
      this.lastFocused.setAttribute('aria-expanded', 'true');
    }
    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    this.modalOpen = true;
    this.lockBodyScroll();
    this.pauseForInteraction();
    this.stopAutoplay();

    var closeBtn = this.modal.querySelector('[data-modal-close-btn]');
    window.setTimeout(function () {
      if (closeBtn) closeBtn.focus();
    }, 0);
  };

  AoneTestimonials.prototype.closeReviewModal = function (skipFocus) {
    if (!this.modal || !this.modalOpen) return;

    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    this.modalOpen = false;
    this.unlockBodyScroll();

    if (this.lastFocused && this.lastFocused.setAttribute) {
      this.lastFocused.setAttribute('aria-expanded', 'false');
      if (!skipFocus && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }
    this.lastFocused = null;
    this.resumeAfterIdle();
  };

  AoneTestimonials.prototype.lockBodyScroll = function () {
    if (this.bodyScrollLocked) return;
    this.bodyScrollLocked = true;
    this.prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  AoneTestimonials.prototype.unlockBodyScroll = function () {
    if (!this.bodyScrollLocked) return;
    this.bodyScrollLocked = false;
    document.body.style.overflow = this.prevBodyOverflow || '';
  };

  AoneTestimonials.prototype.onDocKeydown = function (e) {
    if (!e || e.key !== 'Escape') return;
    if (this.modalOpen) {
      e.preventDefault();
      this.closeReviewModal();
    }
  };

  AoneTestimonials.prototype.onSelectionChange = function () {
    var sel = window.getSelection && window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return;
    var node = sel.anchorNode;
    if (!node) return;
    var el = node.nodeType === 1 ? node : node.parentElement;
    if (el && this.root.contains(el)) {
      this.pauseForInteraction();
      this.resumeAfterIdle();
    }
  };

  AoneTestimonials.prototype.onModalKeydown = function () {};

  /**
   * @param {ParentNode} [scope]
   */
  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!(el instanceof HTMLElement)) continue;
      var id = el.getAttribute('data-section-id') || el.id || String(i);

      var existing = el._aoneTestimonialsController || (id ? instances.get(id) : null);
      if (existing) {
        existing.destroy();
      }
      el._aoneTestimonialsController = null;
      if (id) instances.delete(id);

      var inst = new AoneTestimonials(el);
      el._aoneTestimonialsController = inst;
      instances.set(id, inst);
    }
  }

  /**
   * @param {ParentNode} [scope]
   */
  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var id = el.getAttribute('data-section-id') || el.id;
      var inst = el._aoneTestimonialsController || (id ? instances.get(id) : null);
      if (inst) {
        inst.destroy();
      }
      if (el._aoneTestimonialsController) el._aoneTestimonialsController = null;
      if (id) instances.delete(id);
    }
  }

  function onReady() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    destroyIn(event.target);
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event || !event.target) return;
    destroyIn(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    /* instances stay keyed by section id */
  });
})();
